import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { RefreshCw, XCircle, AlertTriangle, Loader2, Edit } from 'lucide-react';
import { formatDate } from '../../lib/utils';

export function ContractLeaveModals({
  employee,
  showRenewModal, setShowRenewModal, renewalData, setRenewalData, handleRenewContract,
  showEndModal, setShowEndModal, endReason, setEndReason, handleEndContract,
  showEditLeaveModal, setShowEditLeaveModal, leaveBalanceForm, setLeaveBalanceForm,
  handleSaveLeaveBalance, savingLeaveBalance,
}) {
  return (
    <>
            {/* Contract Renewal Modal */}
            <Dialog open={showRenewModal} onOpenChange={setShowRenewModal}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-emerald-400">Renew Contract</DialogTitle>
                  <DialogDescription>
                    Extend {employee?.full_name}'s contract with new dates.
                  </DialogDescription>
                </DialogHeader>
          
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Current Contract End Date</Label>
                    <Input value={formatDate(employee?.contract_end_date)} disabled />
                  </div>
            
                  <div className="space-y-2">
                    <Label>New Start Date (optional)</Label>
                    <Input
                      type="date"
                      value={renewalData.new_start_date}
                      onChange={(e) => setRenewalData({...renewalData, new_start_date: e.target.value})}
                      data-testid="renewal-new-start"
                    />
                    <p className="text-xs text-muted-foreground">Leave blank to continue from current end date</p>
                  </div>
            
                  <div className="space-y-2">
                    <Label>New End Date *</Label>
                    <Input
                      type="date"
                      value={renewalData.new_end_date}
                      onChange={(e) => setRenewalData({...renewalData, new_end_date: e.target.value})}
                      required
                      data-testid="renewal-new-end"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Renewal Reason</Label>
                    <Select 
                      value={renewalData.renewal_reason} 
                      onValueChange={(v) => setRenewalData({...renewalData, renewal_reason: v})}
                    >
                      <SelectTrigger data-testid="renewal-reason">
                        <SelectValue placeholder="Select reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Project extension">Project extension</SelectItem>
                        <SelectItem value="Performance satisfactory">Performance satisfactory</SelectItem>
                        <SelectItem value="Continued business need">Continued business need</SelectItem>
                        <SelectItem value="Permanent position pending">Permanent position pending</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={renewalData.notes}
                      onChange={(e) => setRenewalData({...renewalData, notes: e.target.value})}
                      placeholder="Any additional notes..."
                      data-testid="renewal-notes"
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowRenewModal(false)}>Cancel</Button>
                  <Button onClick={handleRenewContract} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-renewal-btn">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Renew Contract
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* End Contract Modal */}
            <Dialog open={showEndModal} onOpenChange={setShowEndModal}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-red-400">End Contract</DialogTitle>
                  <DialogDescription>
                    This will mark {employee?.full_name}'s contract as ended and set their status to Inactive.
                  </DialogDescription>
                </DialogHeader>
          
                <div className="space-y-4 py-4">
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-400">Warning</p>
                        <p className="text-sm text-muted-foreground">
                          This action will end the contract and mark the employee as inactive. The contract history will be preserved.
                        </p>
                      </div>
                    </div>
                  </div>
            
                  <div className="space-y-2">
                    <Label>Reason for Ending Contract</Label>
                    <Select value={endReason} onValueChange={setEndReason}>
                      <SelectTrigger data-testid="end-reason">
                        <SelectValue placeholder="Select reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Contract ended">Contract ended (natural expiry)</SelectItem>
                        <SelectItem value="Project completed">Project completed</SelectItem>
                        <SelectItem value="Budget constraints">Budget constraints</SelectItem>
                        <SelectItem value="Performance issues">Performance issues</SelectItem>
                        <SelectItem value="Contractor resignation">Contractor resignation</SelectItem>
                        <SelectItem value="Business restructuring">Business restructuring</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowEndModal(false)}>Cancel</Button>
                  <Button variant="destructive" onClick={handleEndContract} data-testid="confirm-end-btn">
                    <XCircle className="w-4 h-4 mr-2" />
                    End Contract
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit Leave Balance Modal */}
            <Dialog open={showEditLeaveModal} onOpenChange={setShowEditLeaveModal}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Edit Leave Balance</DialogTitle>
                  <DialogDescription>
                    Manually adjust {employee?.full_name}'s leave balance. All changes are recorded in the audit log.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="leave-annual">Annual (days)</Label>
                      <Input
                        id="leave-annual"
                        type="number"
                        min="0"
                        step="0.5"
                        value={leaveBalanceForm.annual}
                        onChange={(e) => setLeaveBalanceForm({ ...leaveBalanceForm, annual: e.target.value })}
                        data-testid="input-leave-annual"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="leave-sick">Sick (days)</Label>
                      <Input
                        id="leave-sick"
                        type="number"
                        min="0"
                        step="0.5"
                        value={leaveBalanceForm.sick}
                        onChange={(e) => setLeaveBalanceForm({ ...leaveBalanceForm, sick: e.target.value })}
                        data-testid="input-leave-sick"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="leave-study">Family Resp. (days)</Label>
                      <Input
                        id="leave-study"
                        type="number"
                        min="0"
                        step="0.5"
                        value={leaveBalanceForm.study}
                        onChange={(e) => setLeaveBalanceForm({ ...leaveBalanceForm, study: e.target.value })}
                        data-testid="input-leave-study"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="leave-unpaid">Unpaid (days)</Label>
                      <Input
                        id="leave-unpaid"
                        type="number"
                        min="0"
                        step="0.5"
                        value={leaveBalanceForm.unpaid}
                        onChange={(e) => setLeaveBalanceForm({ ...leaveBalanceForm, unpaid: e.target.value })}
                        data-testid="input-leave-unpaid"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="leave-reason">Reason for adjustment (optional)</Label>
                    <Textarea
                      id="leave-reason"
                      placeholder="e.g. carry-over from 2025, annual top-up..."
                      value={leaveBalanceForm.reason}
                      onChange={(e) => setLeaveBalanceForm({ ...leaveBalanceForm, reason: e.target.value })}
                      data-testid="input-leave-reason"
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowEditLeaveModal(false)} disabled={savingLeaveBalance}>Cancel</Button>
                  <Button onClick={handleSaveLeaveBalance} disabled={savingLeaveBalance} data-testid="save-leave-balance-btn">
                    {savingLeaveBalance ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Edit className="w-4 h-4 mr-2" />}
                    Save Balances
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
    </>
  );
}
